import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Eye, Power, PowerOff, PencilLine, RefreshCw, Search, ChevronRight, ChevronDown, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const TIPOS = [
  { key: "receita", label: "Receita" },
  { key: "despesa", label: "Despesa" },
];
const TIPO_LABEL = Object.fromEntries(TIPOS.map((t) => [t.key, t.label]));

const NATUREZAS_DRE = [
  { key: "receita", label: "Receita" },
  { key: "deducao_receita", label: "Dedução de Receita" },
  { key: "custo_direto", label: "Custo Direto" },
  { key: "despesa_operacional", label: "Despesa Operacional" },
  { key: "patrimonial", label: "Patrimonial" },
  { key: "outros", label: "Outros" },
];
const NATUREZA_DRE_LABEL = Object.fromEntries(NATUREZAS_DRE.map((n) => [n.key, n.label]));

const emptyForm = () => ({
  codigo: "", nome: "", tipo: "despesa", parent_id: "",
  ordem: 0, descricao: "",
});

function StatusBadge({ ativo }) {
  return (
    <span
      data-testid={ativo ? "pc-status-ativa" : "pc-status-inativa"}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
        ativo
          ? "bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20"
          : "bg-stone-100 text-stone-500 border border-stone-300"
      }`}
    >
      {ativo ? "Ativa" : "Inativa"}
    </span>
  );
}

function TipoTag({ tipo }) {
  const rec = tipo === "receita";
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold ${
        rec ? "bg-[#EAF0EA] text-[#083A6B]" : "bg-[#F5E4DD] text-[#7c3a20]"
      }`}
    >
      {rec ? "Receita" : "Despesa"}
    </span>
  );
}

export default function PlanoContas() {
  const [flat, setFlat] = useState([]);
  const [tree, setTree] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);

  const [visao, setVisao] = useState("arvore"); // "arvore" | "lista"
  const [expandidos, setExpandidos] = useState({}); // id -> bool
  const [busca, setBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("all");
  const [filtroStatus, setFiltroStatus] = useState("all"); // all | ativa | inativa
  const [filtroDRE, setFiltroDRE] = useState("all"); // all | in | out
  const [filtroDecisao, setFiltroDecisao] = useState("all"); // all | sim | nao
  const [filtroNaturezaDRE, setFiltroNaturezaDRE] = useState("all");

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);

  const [confirmAlvo, setConfirmAlvo] = useState(null);
  const [toggling, setToggling] = useState(false);

  const podeEscrever = !!(me && (me.is_admin || me.is_finance));

  const load = useCallback(async () => {
    setLoading(true); setErroCarga(null);
    try {
      const [lista, arv, u] = await Promise.all([
        api.get("/plano-contas", { params: { tree: false } }).then((r) => r.data).catch(() => []),
        api.get("/plano-contas", { params: { tree: true } }).then((r) => r.data).catch(() => []),
        api.get("/auth/me").then((r) => r.data).catch(() => null),
      ]);
      setFlat(Array.isArray(lista) ? lista : []);
      setTree(Array.isArray(arv) ? arv : []);
      setMe(u);
      // expande raízes por padrão
      const exp = {};
      (Array.isArray(arv) ? arv : []).forEach((r) => { exp[r.id] = true; });
      setExpandidos((prev) => ({ ...exp, ...prev }));
    } catch (err) {
      setErroCarga(err?.response?.status === 401
        ? "Sessão expirada. Faça login novamente."
        : "Não foi possível carregar o Plano de Contas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flatIdMap = useMemo(() => Object.fromEntries(flat.map((n) => [n.id, n])), [flat]);

  const matchFiltros = useCallback((n) => {
    const q = busca.trim().toLowerCase();
    if (q) {
      const alvo = [n.codigo, n.nome, n.descricao, NATUREZA_DRE_LABEL[n.natureza_dre]].filter(Boolean).join(" ").toLowerCase();
      if (!alvo.includes(q)) return false;
    }
    if (filtroTipo !== "all" && n.tipo !== filtroTipo) return false;
    if (filtroStatus !== "all") {
      const at = filtroStatus === "ativa";
      if (Boolean(n.ativo) !== at) return false;
    }
    if (filtroDRE === "in" && !n.entra_dre) return false;
    if (filtroDRE === "out" && n.entra_dre) return false;
    if (filtroDecisao === "sim" && !n.requer_decisao_gerencial) return false;
    if (filtroDecisao === "nao" && n.requer_decisao_gerencial) return false;
    if (filtroNaturezaDRE !== "all" && n.natureza_dre !== filtroNaturezaDRE) return false;
    return true;
  }, [busca, filtroTipo, filtroStatus, filtroDRE, filtroDecisao, filtroNaturezaDRE]);

  const listaFiltrada = useMemo(() => flat.filter(matchFiltros).sort(
    (a, b) => (a.codigo || "").localeCompare(b.codigo || "", undefined, { numeric: true })
  ), [flat, matchFiltros]);

  // Árvore filtrada: mantém ancestrais quando um descendente combina
  const arvoreFiltrada = useMemo(() => {
    const idsMatch = new Set(listaFiltrada.map((n) => n.id));
    // adicionar ancestrais
    listaFiltrada.forEach((n) => {
      let p = n.parent_id;
      while (p && flatIdMap[p]) {
        idsMatch.add(p);
        p = flatIdMap[p].parent_id;
      }
    });
    const podar = (nodes) => nodes
      .map((n) => {
        const children = podar(n.children || []);
        if (idsMatch.has(n.id) || children.length > 0) return { ...n, children };
        return null;
      })
      .filter(Boolean);
    return podar(tree);
  }, [tree, listaFiltrada, flatIdMap]);

  const toggle = (id) => setExpandidos((s) => ({ ...s, [id]: !s[id] }));

  const abrirNovo = () => {
    setEditingId(null); setForm(emptyForm()); setFormOpen(true);
  };
  const abrirEdicao = (n) => {
    setEditingId(n.id);
    setForm({
      codigo: n.codigo || "", nome: n.nome || "", tipo: n.tipo || "despesa",
      parent_id: n.parent_id || "", ordem: n.ordem ?? 0, descricao: n.descricao || "",
    });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.codigo.trim()) return toast.error("Informe o código");
    if (!form.nome.trim()) return toast.error("Informe o nome");
    setSaving(true);
    try {
      if (editingId) {
        const payload = {
          codigo: form.codigo.trim(),
          nome: form.nome.trim(),
          parent_id: form.parent_id || null,
          ordem: Number(form.ordem || 0),
          descricao: form.descricao.trim(),
        };
        await api.patch(`/plano-contas/${editingId}`, payload);
        toast.success("Conta atualizada");
      } else {
        const payload = {
          codigo: form.codigo.trim(),
          nome: form.nome.trim(),
          tipo: form.tipo,
          parent_id: form.parent_id || null,
          ordem: Number(form.ordem || 0),
          descricao: form.descricao.trim(),
        };
        await api.post("/plano-contas", payload);
        toast.success("Conta cadastrada");
      }
      setFormOpen(false); setEditingId(null); setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || "Erro ao salvar. Verifique os dados."));
    } finally {
      setSaving(false);
    }
  };

  const abrirDetalhe = async (n) => {
    setDetalheAlvo(n); setDetalheOpen(true); setDetalheLoading(true);
    try {
      const { data } = await api.get(`/plano-contas/${n.id}`);
      setDetalheAlvo(data);
    } catch { /* mantém básico */ }
    finally { setDetalheLoading(false); }
  };

  const confirmarToggle = (n) => setConfirmAlvo(n);
  const executarToggle = async () => {
    if (!confirmAlvo) return;
    const acao = confirmAlvo.ativo ? "desativar" : "ativar";
    setToggling(true);
    try {
      await api.post(`/plano-contas/${confirmAlvo.id}/${acao}`);
      toast.success(acao === "desativar" ? "Conta desativada" : "Conta ativada");
      setConfirmAlvo(null);
      load();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || `Erro ao ${acao} a conta`));
    } finally {
      setToggling(false);
    }
  };

  // Options para parent_id no formulário: apenas contas do mesmo tipo (para create) e não descendentes (para edit — simplificado)
  const parentOptions = useMemo(() => {
    const tipo = form.tipo;
    return flat.filter((n) => n.tipo === tipo && n.id !== editingId);
  }, [flat, form.tipo, editingId]);

  const nomeParent = (id) => (id && flatIdMap[id]) ? `${flatIdMap[id].codigo} · ${flatIdMap[id].nome}` : "—";

  const renderNode = (n, depth) => {
    const temFilhos = (n.children || []).length > 0;
    const aberto = !!expandidos[n.id];
    return (
      <div key={n.id} data-testid={`pc-node-${n.id}`}>
        <div
          className={`grid grid-cols-[24px_100px_1fr_auto_auto_auto_auto_auto] gap-3 items-center py-2 px-3 rounded-md hover:bg-stone-50 ${
            !n.ativo ? "opacity-60" : ""
          }`}
          style={{ paddingLeft: `${12 + depth * 20}px` }}
        >
          <button
            onClick={() => temFilhos && toggle(n.id)}
            className={`text-stone-500 ${temFilhos ? "hover:text-stone-800" : "opacity-0 cursor-default"}`}
            aria-label={temFilhos ? (aberto ? "Recolher" : "Expandir") : ""}
            data-testid={temFilhos ? `pc-toggle-${n.id}` : undefined}
          >
            {temFilhos ? (aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="w-3 inline-block" />}
          </button>
          <span className="font-mono text-xs text-stone-600">{n.codigo}</span>
          <span className="text-stone-900 text-sm truncate">
            {n.nome}
            {n.requer_decisao_gerencial && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#8B6A1F]" data-testid={`pc-decisao-${n.id}`}>
                <AlertCircle size={11} /> decisão gerencial
              </span>
            )}
          </span>
          <TipoTag tipo={n.tipo} />
          <span className={`text-[10px] uppercase tracking-widest ${n.entra_dre ? "text-[#083A6B]" : "text-stone-400"}`}>
            {n.entra_dre ? "DRE" : "fora DRE"}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-stone-500 hidden lg:inline">
            {NATUREZA_DRE_LABEL[n.natureza_dre] || "—"}
          </span>
          <StatusBadge ativo={n.ativo} />
          <div className="inline-flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(n)} aria-label={`Detalhes ${n.codigo}`} data-testid={`btn-pc-detalhe-${n.id}`}>
              <Eye size={13} />
            </Button>
            {podeEscrever && (
              <>
                <Button size="sm" variant="ghost" onClick={() => abrirEdicao(n)} aria-label={`Editar ${n.codigo}`} data-testid={`btn-pc-editar-${n.id}`}>
                  <PencilLine size={13} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => confirmarToggle(n)}
                  aria-label={`${n.ativo ? "Desativar" : "Ativar"} ${n.codigo}`}
                  data-testid={`btn-pc-toggle-${n.id}`}>
                  {n.ativo ? <PowerOff size={13} /> : <Power size={13} />}
                </Button>
              </>
            )}
          </div>
        </div>
        {temFilhos && aberto && (n.children || []).map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div data-testid="page-plano-contas">
      <PageHeader
        eyebrow="Financeiro · Metadados"
        title="Plano de Contas"
        description="Fonte oficial das categorias financeiras. Metadados usados por DRE, Rentabilidade e Movimentos."
        actions={
          podeEscrever ? (
            <Dialog open={formOpen} onOpenChange={(v) => { setFormOpen(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
              <DialogTrigger asChild>
                <Button data-testid="btn-nova-pc" onClick={abrirNovo}><Plus size={16} /> Nova conta</Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Editar conta do Plano" : "Nova conta do Plano"}</DialogTitle>
                  <DialogDescription>
                    Estruture o Plano de Contas. Os metadados DRE (entra_dre, natureza_dre, decisão gerencial)
                    são gerenciados pelo backend e não podem ser editados aqui.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pc-codigo">Código *</Label>
                    <Input id="pc-codigo" data-testid="input-pc-codigo" value={form.codigo}
                      onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                      placeholder="Ex.: 2.1.9" required />
                  </div>
                  <div>
                    <Label htmlFor="pc-tipo">Tipo *</Label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v, parent_id: "" })} disabled={!!editingId}>
                      <SelectTrigger id="pc-tipo" data-testid="select-pc-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {editingId && <p className="text-[11px] text-stone-500 mt-1">Tipo não é editável.</p>}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="pc-nome">Nome *</Label>
                    <Input id="pc-nome" data-testid="input-pc-nome" value={form.nome}
                      onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="pc-parent">Conta pai (opcional)</Label>
                    <Select value={form.parent_id || "none"}
                      onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                      <SelectTrigger id="pc-parent" data-testid="select-pc-parent"><SelectValue placeholder="— Raiz" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Raiz (sem pai)</SelectItem>
                        {parentOptions.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.codigo} · {p.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-stone-500 mt-1">O tipo do filho deve coincidir com o do pai.</p>
                  </div>
                  <div>
                    <Label htmlFor="pc-ordem">Ordem</Label>
                    <Input id="pc-ordem" type="number" data-testid="input-pc-ordem" value={form.ordem}
                      onChange={(e) => setForm({ ...form, ordem: e.target.value })} />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="pc-desc">Descrição</Label>
                    <Input id="pc-desc" data-testid="input-pc-descricao" value={form.descricao}
                      onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
                  </div>
                  <DialogFooter className="md:col-span-2">
                    <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
                    <Button type="submit" data-testid="btn-salvar-pc" disabled={saving}>
                      {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar conta"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="filter-pc-busca" value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Código, nome, descrição..." className="pl-8" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Visão</Label>
          <Select value={visao} onValueChange={setVisao}>
            <SelectTrigger data-testid="select-pc-visao" className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="arvore">Árvore</SelectItem>
              <SelectItem value="lista">Lista</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Tipo</Label>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger data-testid="filter-pc-tipo" className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {TIPOS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">DRE</Label>
          <Select value={filtroDRE} onValueChange={setFiltroDRE}>
            <SelectTrigger data-testid="filter-pc-dre" className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="in">Entra DRE</SelectItem>
              <SelectItem value="out">Fora DRE</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Natureza DRE</Label>
          <Select value={filtroNaturezaDRE} onValueChange={setFiltroNaturezaDRE}>
            <SelectTrigger data-testid="filter-pc-natdre" className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {NATUREZAS_DRE.map((n) => <SelectItem key={n.key} value={n.key}>{n.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Decisão</Label>
          <Select value={filtroDecisao} onValueChange={setFiltroDecisao}>
            <SelectTrigger data-testid="filter-pc-decisao" className="w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="sim">Requer</SelectItem>
              <SelectItem value="nao">Não requer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
          <Select value={filtroStatus} onValueChange={setFiltroStatus}>
            <SelectTrigger data-testid="filter-pc-status" className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="ativa">Ativa</SelectItem>
              <SelectItem value="inativa">Inativa</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={load} aria-label="Atualizar" data-testid="btn-refresh-pc"><RefreshCw size={14} /></Button>
      </div>

      {/* Corpo */}
      {loading ? (
        <div data-testid="pc-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando plano de contas...
        </div>
      ) : erroCarga ? (
        <div data-testid="pc-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          {erroCarga}
        </div>
      ) : listaFiltrada.length === 0 ? (
        <div data-testid="pc-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center">
          <div className="font-serif text-xl text-stone-900 mb-2">Nenhuma conta do Plano de Contas encontrada.</div>
          <p className="text-sm text-stone-600">Ajuste os filtros ou cadastre uma nova conta.</p>
        </div>
      ) : visao === "arvore" ? (
        <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-2 overflow-x-auto" data-testid="pc-arvore">
          <div className="min-w-[720px]">
            {arvoreFiltrada.map((n) => renderNode(n, 0))}
          </div>
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3" data-testid="pc-lista-mobile">
            {listaFiltrada.map((n) => (
              <div key={n.id} data-testid={`pc-card-${n.id}`}
                className={`bg-[#FFFFFF] border border-stone-200 rounded-lg p-4 ${!n.ativo ? "opacity-70" : ""}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-stone-500">{n.codigo}</div>
                    <div className="font-serif text-lg text-stone-900 truncate">{n.nome}</div>
                  </div>
                  <StatusBadge ativo={n.ativo} />
                </div>
                <div className="flex flex-wrap gap-2 text-[11px] mb-3">
                  <TipoTag tipo={n.tipo} />
                  <span className={`px-2 py-0.5 rounded ${n.entra_dre ? "bg-[#EAF0EA] text-[#083A6B]" : "bg-stone-100 text-stone-500"}`}>
                    {n.entra_dre ? "Entra DRE" : "Fora DRE"}
                  </span>
                  <span className="px-2 py-0.5 rounded bg-stone-100 text-stone-600">
                    {NATUREZA_DRE_LABEL[n.natureza_dre] || "—"}
                  </span>
                  {n.requer_decisao_gerencial && (
                    <span className="px-2 py-0.5 rounded bg-[#8B6A1F]/15 text-[#5c460f]">Decisão gerencial</span>
                  )}
                </div>
                <div className="text-xs text-stone-600 mb-3">
                  {n.parent_id ? <><b>Pai:</b> {nomeParent(n.parent_id)}</> : <span className="text-stone-400">Conta raiz</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => abrirDetalhe(n)} data-testid={`btn-pc-detalhe-${n.id}`}>
                    <Eye size={14} /> Detalhes
                  </Button>
                  {podeEscrever && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => abrirEdicao(n)} data-testid={`btn-pc-editar-${n.id}`}>
                        <PencilLine size={14} /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => confirmarToggle(n)} data-testid={`btn-pc-toggle-${n.id}`}>
                        {n.ativo ? <><PowerOff size={14} /> Desativar</> : <><Power size={14} /> Ativar</>}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tabela */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto" data-testid="pc-lista-desktop">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Código</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Nome</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">DRE</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Natureza DRE</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Decisão</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listaFiltrada.map((n) => (
                  <TableRow key={n.id} data-testid={`pc-row-${n.id}`} className={!n.ativo ? "opacity-70" : ""}>
                    <TableCell className="font-mono text-xs text-stone-600">{n.codigo}</TableCell>
                    <TableCell className="text-stone-900 text-sm">
                      {n.nome}
                      {n.parent_id && (
                        <div className="text-[10px] text-stone-400">↳ filho de {nomeParent(n.parent_id)}</div>
                      )}
                    </TableCell>
                    <TableCell><TipoTag tipo={n.tipo} /></TableCell>
                    <TableCell>
                      <span className={`text-[10px] uppercase tracking-widest ${n.entra_dre ? "text-[#083A6B]" : "text-stone-400"}`}>
                        {n.entra_dre ? "Entra" : "Fora"}
                      </span>
                    </TableCell>
                    <TableCell className="text-stone-700 text-xs">{NATUREZA_DRE_LABEL[n.natureza_dre] || "—"}</TableCell>
                    <TableCell>
                      {n.requer_decisao_gerencial ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#8B6A1F]" data-testid={`pc-row-decisao-${n.id}`}>
                          <AlertCircle size={11} /> requer
                        </span>
                      ) : <span className="text-stone-400 text-[10px]">—</span>}
                    </TableCell>
                    <TableCell><StatusBadge ativo={n.ativo} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(n)} aria-label={`Detalhes ${n.codigo}`} data-testid={`btn-pc-detalhe-${n.id}`}>
                          <Eye size={13} />
                        </Button>
                        {podeEscrever && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => abrirEdicao(n)} aria-label={`Editar ${n.codigo}`} data-testid={`btn-pc-editar-${n.id}`}>
                              <PencilLine size={13} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => confirmarToggle(n)}
                              aria-label={`${n.ativo ? "Desativar" : "Ativar"} ${n.codigo}`} data-testid={`btn-pc-toggle-${n.id}`}>
                              {n.ativo ? <PowerOff size={13} /> : <Power size={13} />}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Detalhes */}
      <Dialog open={detalheOpen} onOpenChange={(v) => { setDetalheOpen(v); if (!v) setDetalheAlvo(null); }}>
        <DialogContent className="max-w-xl" data-testid="dialog-pc-detalhe">
          <DialogHeader>
            <DialogTitle>{detalheAlvo?.codigo} · {detalheAlvo?.nome}</DialogTitle>
            <DialogDescription>Metadados do Plano de Contas conforme retorno do backend.</DialogDescription>
          </DialogHeader>
          {detalheLoading ? (
            <div className="text-center text-stone-500 py-6">Carregando...</div>
          ) : detalheAlvo ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><div className="text-[11px] uppercase text-stone-500">Tipo</div><TipoTag tipo={detalheAlvo.tipo} /></div>
              <div><div className="text-[11px] uppercase text-stone-500">Status</div><StatusBadge ativo={detalheAlvo.ativo} /></div>
              <div><div className="text-[11px] uppercase text-stone-500">Entra DRE</div>{detalheAlvo.entra_dre ? "Sim" : "Não"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Natureza DRE</div>{NATUREZA_DRE_LABEL[detalheAlvo.natureza_dre] || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Requer decisão gerencial</div>{detalheAlvo.requer_decisao_gerencial ? "Sim" : "Não"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Ordem</div>{detalheAlvo.ordem ?? 0}</div>
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-stone-500">Conta pai</div>
                {detalheAlvo.parent_id ? nomeParent(detalheAlvo.parent_id) : <span className="text-stone-400">Raiz</span>}
              </div>
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-stone-500">Descrição</div>
                {detalheAlvo.descricao || "—"}
              </div>
              <div className="col-span-2 text-[11px] text-stone-500 pt-2 border-t border-stone-200">
                Criada em {detalheAlvo.created_at?.slice(0, 10) || "—"} · Atualizada em {detalheAlvo.updated_at?.slice(0, 10) || "—"}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Confirmação ativar/desativar */}
      <Dialog open={!!confirmAlvo} onOpenChange={(v) => { if (!v) setConfirmAlvo(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-pc-toggle">
          <DialogHeader>
            <DialogTitle>
              {confirmAlvo?.ativo ? "Desativar esta conta?" : "Ativar esta conta?"}
            </DialogTitle>
            <DialogDescription>
              {confirmAlvo?.ativo
                ? "A conta permanece registrada. Se possuir filhos ativos, o backend bloqueará a desativação."
                : "A conta voltará a aparecer como disponível para os módulos financeiros."}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-stone-700">
            <b>{confirmAlvo?.codigo}</b> · {confirmAlvo?.nome}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAlvo(null)} disabled={toggling}>Cancelar</Button>
            <Button data-testid="btn-pc-confirmar-toggle" onClick={executarToggle} disabled={toggling}>
              {toggling ? "Processando..." : confirmAlvo?.ativo ? "Desativar" : "Ativar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
